/**
 * The tablet's side of who is signed in.
 *
 * Two facts about these eleven devices drive every decision here.
 *
 * A tablet is a wall fixture, not a phone. It gets unplugged, it reboots, the
 * janitor power-cycles the strip over Christmas — and it must come back up as
 * the same teacher, ringing the same wing, without anyone typing anything. So
 * the session and the device id are persisted, and a teacher session is issued
 * for months rather than hours.
 *
 * Its clock is not to be trusted. The tablets are guests on a network where NTP
 * is blocked, so `Date.now()` can be years off after a battery-flat reboot —
 * exactly the moment we are asked "is this session still valid?". A naive
 * expiry check would answer "no" and leave a classroom staring at a PIN pad it
 * cannot fill in. So expiry here is deliberately lopsided: a teacher session is
 * given the benefit of the doubt when the clock is visibly nonsense, and an
 * admin session never is. The server re-checks the real expiry on every
 * privileged call anyway, which is where the authority actually lives.
 */

import type { AuthResponse, Session, SetupStatus, Wing } from '../shared/types';

const SESSION_KEY = 'valor.bells.session.v1';
/** The room's own teacher, kept aside so an expiring admin session restores it. */
const ROOM_KEY = 'valor.bells.room-session.v1';
const DEVICE_KEY = 'valor.bells.device.v1';

/** Nothing on this pad may hang forever on a dead network. */
const REQUEST_TIMEOUT_MS = 12_000;

/** localStorage throws in a locked-down webview; the day must still work. */
const memory = new Map<string, string>();

function readLocal(key: string): string | null {
  try {
    const value = window.localStorage.getItem(key);
    if (value !== null) return value;
  } catch {
    /* fall through to the in-memory copy */
  }
  return memory.get(key) ?? null;
}

function writeLocal(key: string, value: string): void {
  memory.set(key, value);
  try {
    window.localStorage.setItem(key, value);
  } catch {
    /* in-memory only for this boot */
  }
}

function removeLocal(key: string): void {
  memory.delete(key);
  try {
    window.localStorage.removeItem(key);
  } catch {
    /* nothing to do */
  }
}

/** A bell group id. The office creates these, so any non-empty string is one. */
function isWing(value: unknown): value is Wing {
  return typeof value === 'string' && value.trim() !== '';
}

function parseSession(raw: string | null): Session | null {
  if (!raw) return null;
  try {
    const value = JSON.parse(raw) as Partial<Session>;
    if (!value || typeof value.token !== 'string' || !value.token) return null;
    if (typeof value.teacherId !== 'string' || !value.teacherId) return null;
    if (!isWing(value.wing)) return null;
    if (value.role !== 'teacher' && value.role !== 'admin') return null;
    if (typeof value.expiresAt !== 'number') return null;
    return {
      token: value.token,
      teacherId: value.teacherId,
      name: typeof value.name === 'string' ? value.name : '',
      wing: value.wing,
      roomLabel: typeof value.roomLabel === 'string' ? value.roomLabel : '',
      role: value.role,
      expiresAt: value.expiresAt,
    };
  } catch {
    return null;
  }
}

/** Before this, the device clock is lying, not reporting. */
const CLOCK_FLOOR_MS = Date.UTC(2025, 0, 1);

/**
 * An admin session expires the moment it says it does — an admin left signed in
 * on a classroom wall is exactly what that short expiry guards against.
 *
 * A teacher session gets one concession. A tablet that comes back from a power
 * cut with its clock reset to the ROM's build date is telling us the clock is
 * wrong, not that the teacher's session ended, and the room stays signed in.
 * Ringing the right bells beats being pedantic about a clock we already know
 * lies; the server re-checks the real expiry on anything that matters.
 */
function isUsable(session: Session): boolean {
  const now = Date.now();
  if (session.role === 'admin') return now <= session.expiresAt;
  if (now < CLOCK_FLOOR_MS) return true;
  return now <= session.expiresAt;
}

export function loadSession(): Session | null {
  const current = parseSession(readLocal(SESSION_KEY));
  if (current && isUsable(current)) return current;

  // The admin session on this wall has run out. Hand the room back to its own
  // teacher rather than dropping it to a PIN pad nobody in the room can fill.
  const room = parseSession(readLocal(ROOM_KEY));
  if (room && isUsable(room)) {
    writeLocal(SESSION_KEY, JSON.stringify(room));
    return room;
  }

  if (current) removeLocal(SESSION_KEY);
  return null;
}

export function saveSession(s: Session): void {
  writeLocal(SESSION_KEY, JSON.stringify(s));
  if (s.role === 'teacher') writeLocal(ROOM_KEY, JSON.stringify(s));
}

/** An explicit sign-out means this tablet belongs to nobody. Clear both. */
export function clearSession(): void {
  removeLocal(SESSION_KEY);
  removeLocal(ROOM_KEY);
}

function randomId(): string {
  try {
    if (typeof crypto?.randomUUID === 'function') return crypto.randomUUID();
    const bytes = crypto.getRandomValues(new Uint8Array(16));
    return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

/**
 * Stable for the life of the tablet. It is what makes rate limiting per-room
 * instead of per-school, and what lets the office tell tablet from tablet.
 */
export function deviceId(): string {
  let id = readLocal(DEVICE_KEY);
  if (!id) {
    id = `tab_${randomId()}`;
    writeLocal(DEVICE_KEY, id);
  }
  return id;
}

async function post(path: string, body: unknown, headers: Record<string, string> = {}): Promise<Response> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), REQUEST_TIMEOUT_MS);
  try {
    return await fetch(path, {
      method: 'POST',
      headers: { 'content-type': 'application/json', ...headers },
      body: JSON.stringify(body),
      cache: 'no-store',
      signal: abort.signal,
    });
  } finally {
    clearTimeout(timer);
  }
}

async function asAuthResponse(res: Response): Promise<AuthResponse> {
  try {
    const parsed = (await res.json()) as AuthResponse;
    if (parsed && typeof parsed.ok === 'boolean') return parsed;
  } catch {
    /* fall through */
  }
  return { ok: false, error: `Sign-in failed (${res.status}).` };
}

/**
 * Send a PIN. `requireAdmin` is for the office gate: the server refuses to mint
 * anything at all for a teacher PIN there, so a wrong-door attempt never leaves
 * a stray session behind on the wall.
 */
export async function submitPin(
  pin: string,
  opts: { requireAdmin?: boolean } = {},
): Promise<AuthResponse> {
  let result: AuthResponse;
  try {
    const res = await post('/api/auth/pin', {
      pin,
      deviceId: deviceId(),
      ...(opts.requireAdmin ? { requireRole: 'admin' } : {}),
    });
    result = await asAuthResponse(res);
  } catch {
    return { ok: false, error: 'No answer from the school server. Check the Wi-Fi.' };
  }

  if (result.ok && result.session) {
    saveSession(result.session);
    void noteDeviceSeen(result.session);
  }
  return result;
}

/** Fire and forget: the office wants to know which tablet is in which room. */
function noteDeviceSeen(session: Session): void {
  try {
    const body = JSON.stringify({
      deviceId: deviceId(),
      teacherId: session.teacherId,
      name: session.name,
      wing: session.wing,
      roomLabel: session.roomLabel,
      at: Date.now(),
    });
    // keepalive so a reload immediately after sign-in does not cancel it.
    void fetch('/api/device/seen', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body,
      keepalive: true,
      cache: 'no-store',
    }).catch(() => undefined);
  } catch {
    /* a check-in is never worth failing a sign-in over */
  }
}

/**
 * Fails CLOSED on purpose. If the server cannot be reached we report "no setup
 * needed" — offering to bootstrap a school that is merely offline would invite
 * someone to set a brand new admin PIN over a flaky Wi-Fi.
 */
export async function setupStatus(): Promise<SetupStatus> {
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), REQUEST_TIMEOUT_MS);
  try {
    const res = await fetch('/api/setup/status', { cache: 'no-store', signal: abort.signal });
    if (!res.ok) return { needsSetup: false };
    const parsed = (await res.json()) as Partial<SetupStatus>;
    return { needsSetup: parsed?.needsSetup === true };
  } catch {
    return { needsSetup: false };
  } finally {
    clearTimeout(timer);
  }
}

export async function runSetup(adminPin: string, officePin: string): Promise<AuthResponse> {
  let result: AuthResponse;
  try {
    result = await asAuthResponse(await post('/api/setup', { adminPin, officePin }));
  } catch {
    return { ok: false, error: 'No answer from the school server. Check the Wi-Fi.' };
  }
  if (result.ok && result.session) saveSession(result.session);
  return result;
}

/** Bearer token for the admin routes. Empty when nobody is signed in. */
export function authHeader(): Record<string, string> {
  const session = loadSession();
  return session ? { authorization: `Bearer ${session.token}` } : {};
}
